import { Injectable, OnDestroy } from '@angular/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { Subscription } from 'rxjs';

/**
 * Service that plays synthesized audio feedback tones (via Web Audio API) in response to toast notifications.
 */
@Injectable({
  providedIn: 'root',
})
export class AudioService implements OnDestroy {
  private subscription: Subscription;

  /**
   * Initializes the AudioService and subscribes to message service notifications.
   * @param messageService - PrimeNG message service emitting notification toasts.
   */
  constructor(private messageService: MessageService) {
    this.subscription = this.messageService.messageObserver.subscribe(
      (message) => {
        const messages = Array.isArray(message) ? message : [message];
        messages.forEach((msg) => {
          if (msg.severity === 'error') this.playError();
          else if (msg.severity === 'warn') this.playWarn();
        });
      },
    );
  }

  /**
   * Creates and returns a new AudioContext instance.
   * @returns A new Web Audio AudioContext.
   */
  private createContext(): AudioContext {
    return new AudioContext();
  }

  /**
   * Schedules a single oscillator tone on the given AudioContext.
   * @param ctx AudioContext to use for synthesis.
   * @param frequency Tone frequency in Hz.
   * @param startAt Start time in seconds relative to ctx.currentTime.
   * @param duration Duration of the tone in seconds.
   * @param volume Peak gain (0–1), defaults to 0.4.
   */
  private tone(
    ctx: AudioContext,
    frequency: number,
    startAt: number,
    duration: number,
    volume = 0.4,
  ): void {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(frequency, startAt);
    gain.gain.setValueAtTime(volume, startAt);
    gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);

    oscillator.start(startAt);
    oscillator.stop(startAt + duration);
  }

  /** Plays a two-tone descending beep to signal an error message. */
  playError(): void {
    try {
      const ctx = this.createContext();
      this.tone(ctx, 880, ctx.currentTime, 0.15);
      this.tone(ctx, 660, ctx.currentTime + 0.18, 0.2);
    } catch (_) {}
  }

  /** Plays a single short beep to signal a warning message. */
  playWarn(): void {
    try {
      const ctx = this.createContext();
      this.tone(ctx, 520, ctx.currentTime, 0.25);
    } catch (_) {}
  }

  /**
   * Cleans up notification subscriptions on service destruction.
   */
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
